import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// High quality curated images for each category
const MAGAZINE_IMAGES = [
  // World News
  'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=600&q=80',
  'https://images.unsplash.com/photo-1495020689067-958852a7765e?w=600&q=80',
  'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?w=600&q=80',
  // Bollywood / Film
  'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=600&q=80',
  'https://images.unsplash.com/photo-1485846234645-a62644f84728?w=600&q=80',
  'https://images.unsplash.com/photo-1518676590629-3dcbd9c5a5c9?w=600&q=80',
  // Hollywood
  'https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=600&q=80',
  'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=600&q=80',
  'https://images.unsplash.com/photo-1517604931442-7e0c8ed2963c?w=600&q=80',
  // Science
  'https://images.unsplash.com/photo-1507413245164-6160d8298b31?w=600&q=80',
  'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&q=80',
  'https://images.unsplash.com/photo-1614935151651-0bea6508db6b?w=600&q=80',
  // History
  'https://images.unsplash.com/photo-1461360370896-922624d12a74?w=600&q=80',
  'https://images.unsplash.com/photo-1564399580075-5dfe19c205f3?w=600&q=80',
  'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=600&q=80',
  // Technology
  'https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&q=80',
  'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?w=600&q=80',
  'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600&q=80',
  // Travel
  'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=600&q=80',
  'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=600&q=80',
  'https://images.unsplash.com/photo-1530521954074-e64f6810b32d?w=600&q=80',
  // Art & Culture
  'https://images.unsplash.com/photo-1547891654-e66ed7ebb968?w=600&q=80',
  'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=600&q=80',
  'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=600&q=80',
  // Sports
  'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=600&q=80',
  'https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=600&q=80',
  'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=600&q=80',
  // Social Media / People
  'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=600&q=80',
  'https://images.unsplash.com/photo-1562577309-4932fdd64cd1?w=600&q=80',
  'https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=600&q=80',
  // Politics / Buildings
  'https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?w=600&q=80',
  'https://images.unsplash.com/photo-1555848962-6e79363ec58f?w=600&q=80',
  'https://images.unsplash.com/photo-1541872703-74c5e44368f9?w=600&q=80',
  // Mythology / Ancient
  'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=600&q=80',
  'https://images.unsplash.com/photo-1569974507005-6dc61f97fb5c?w=600&q=80',
  // More variety
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80',
  'https://images.unsplash.com/photo-1682687220742-aba13b6e50ba?w=600&q=80',
  'https://images.unsplash.com/photo-1682687221038-404670f01d03?w=600&q=80',
  'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=600&q=80',
  'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=600&q=80',
  'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?w=600&q=80',
];

// Shuffle array helper
const shuffleArray = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

const MagazineBackground = () => {
  const [gridImages, setGridImages] = useState([]);
  const [tick, setTick] = useState(0);
  
  // Grid configuration
  const COLS = 8;
  const ROWS = 5;
  const TOTAL_CELLS = COLS * ROWS;
  
  // Initialize grid with shuffled images
  useEffect(() => {
    const shuffled = shuffleArray(MAGAZINE_IMAGES);
    const initial = [];
    for (let i = 0; i < TOTAL_CELLS; i++) {
      initial.push({
        src: shuffled[i % shuffled.length],
        key: `${i}-${Date.now()}`
      });
    }
    setGridImages(initial);
  }, []);
  
  // Change random cells every 500ms
  useEffect(() => {
    const interval = setInterval(() => {
      setGridImages(prev => {
        if (prev.length === 0) return prev;
        
        const newImages = [...prev];
        // Change 4-6 random cells
        const changeCount = Math.floor(Math.random() * 3) + 4;
        
        for (let i = 0; i < changeCount; i++) {
          const cellIndex = Math.floor(Math.random() * TOTAL_CELLS);
          const imageIndex = Math.floor(Math.random() * MAGAZINE_IMAGES.length);
          newImages[cellIndex] = {
            src: MAGAZINE_IMAGES[imageIndex],
            key: `${cellIndex}-${Date.now()}-${i}`
          };
        }
        
        return newImages;
      });
      
      setTick(t => t + 1);
    }, 500);
    
    return () => clearInterval(interval);
  }, []);
  
  if (gridImages.length === 0) return null;
  
  return (
    <div className="fixed inset-0 overflow-hidden z-0">
      {/* Magazine Grid */}
      <div 
        className="absolute inset-0 grid gap-1"
        style={{
          gridTemplateColumns: `repeat(${COLS}, 1fr)`,
          gridTemplateRows: `repeat(${ROWS}, 1fr)`,
        }}
      >
        {gridImages.map((image, index) => (
          <div 
            key={index}
            className="relative overflow-hidden"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={image.key}
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 0.4, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
                className="absolute inset-0"
              >
                <img
                  src={image.src}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </motion.div>
            </AnimatePresence>
          </div>
        ))}
      </div>
      
      {/* Dark overlays for readability */}
      <div className="absolute inset-0 bg-[#0B0D12]/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D12] via-[#0B0D12]/30 to-[#0B0D12]/80" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0D12]/90 via-transparent to-[#0B0D12]" />
      
      {/* Glow effects */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-purple-600/20 blur-[150px]" />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-cyan-600/20 blur-[120px]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-blue-600/10 blur-[180px]" />
    </div>
  );
};

export default MagazineBackground;
